import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../components/Layout';
import { Loader2, Plus, Trash2, Upload, DollarSign } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CostCodes = () => {
  const { projectId } = useParams();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ code: '', description: '', unitCost: '', unit: 'unit' });
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/projects/${projectId}/cost-codes`);
      setCodes(r.data || []);
    } catch (e) { /* */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [projectId]);

  const add = async () => {
    if (!form.code.trim() || form.unitCost === '') { toast.error('Code and unit cost are required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/projects/${projectId}/cost-codes`, { ...form, unitCost: parseFloat(form.unitCost) });
      setForm({ code: '', description: '', unitCost: '', unit: 'unit' });
      toast.success('Cost code added');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add'); } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await axios.delete(`${API}/cost-codes/${id}`); load(); } catch (e) { toast.error('Delete failed'); }
  };

  const importCsv = async (e) => {
    const file = (e.target.files || [])[0];
    if (fileRef.current) fileRef.current.value = '';
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await axios.post(`${API}/projects/${projectId}/cost-codes/import`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Imported ${r.data.imported} codes${r.data.errors.length ? `, ${r.data.errors.length} rows rejected` : ''}`);
      if (r.data.errors.length) toast.error(`Rejected rows: ${r.data.errors.map((x) => x.row).join(', ')}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Import failed'); } finally { setImporting(false); }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2"><DollarSign className="w-7 h-7 text-emerald-500" /> Cost Codes</h1>
          <div>
            <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={importCsv} data-testid="costcode-csv-input" />
            <button onClick={() => fileRef.current?.click()} disabled={importing} className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-800 flex items-center gap-2 disabled:opacity-60" data-testid="costcode-import-btn">
              {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Import CSV
            </button>
          </div>
        </div>
        <p className="text-sm text-slate-500 -mt-4">CSV columns: <span className="font-mono">code, description, unitCost, unit</span></p>

        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm grid grid-cols-1 sm:grid-cols-5 gap-3 items-end" data-testid="costcode-form">
          <div><label className="text-xs text-slate-500">Code</label><input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg" data-testid="costcode-code" /></div>
          <div><label className="text-xs text-slate-500">Description</label><input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg" data-testid="costcode-desc" /></div>
          <div><label className="text-xs text-slate-500">Unit Cost</label><input type="number" step="0.01" value={form.unitCost} onChange={(e) => setForm({ ...form, unitCost: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg" data-testid="costcode-unitcost" /></div>
          <div><label className="text-xs text-slate-500">Unit</label><input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg" data-testid="costcode-unit" /></div>
          <button onClick={add} disabled={saving} className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center justify-center gap-2 disabled:opacity-60" data-testid="costcode-add-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add</button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-500"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…</div>
        ) : codes.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-400" data-testid="costcode-empty">No cost codes yet. Add one above or import a CSV.</div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="costcode-list">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-slate-500 border-b border-slate-100"><th className="px-5 py-2">Code</th><th className="px-3 py-2">Description</th><th className="px-3 py-2 text-right">Unit Cost</th><th className="px-3 py-2">Unit</th><th className="px-3 py-2"></th></tr></thead>
              <tbody>
                {codes.map((c) => (
                  <tr key={c.id} className="border-b border-slate-50" data-testid="costcode-row">
                    <td className="px-5 py-3 font-medium text-slate-800">{c.code}</td>
                    <td className="px-3 py-3 text-slate-600">{c.description}</td>
                    <td className="px-3 py-3 text-right">{c.unitCost}</td>
                    <td className="px-3 py-3 text-slate-500">{c.unit}</td>
                    <td className="px-3 py-3 text-right"><button onClick={() => remove(c.id)} className="text-slate-400 hover:text-red-500" data-testid="costcode-delete"><Trash2 className="w-4 h-4" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default CostCodes;
